// aifab - Models page
import { useSubmit } from '../hooks/useSubmit';
import { useForm } from '../hooks/useForm';
// Model configs per project + versions + params
import { useState, useEffect } from 'react';
import { useApi } from '../hooks/useApi';
import { apiFetch } from '../hooks/apiFetch';
import { Spinner, StatusBadge, EmptyState, Modal, ModalBody, ModalFooter, Err, Alert, SectionCard, Hint } from '../components/common';


/* ── Model Card Modal ─────────────────────────────────────────────────────── */
function ModelCardModal({ versionId, onClose }) {
  const { data: card, loading } = useApi(`/admin/versions/${versionId}/card`);

  const Section = ({ title, children }) => (
    <div style={{ marginBottom:20 }}>
      <div style={{ fontSize:10, fontWeight:700, textTransform:'uppercase',
        letterSpacing:'.08em', color:'#64748B', marginBottom:10,
        borderBottom:'1px solid var(--bd)', paddingBottom:6 }}>{title}</div>
      {children}
    </div>
  );

  const Row = ({ label, value, mono }) => value != null && value !== '' ? (
    <div style={{ display:'flex', marginBottom:7 }}>
      <div style={{ width:140, fontSize:11, color:'#64748B', flexShrink:0 }}>{label}</div>
      <div style={{ fontSize:12, color:'#0F172A',
        fontFamily: mono ? 'var(--f-mono)' : undefined }}>{String(value)}</div>
    </div>
  ) : null;

  return (
    <Modal title="Model Card" onClose={onClose} xl>
      <ModalBody>
      {loading ? (
        <div style={{ display:'flex', justifyContent:'center', padding:40 }}><Spinner /></div>
      ) : !card ? (
        <div style={{ color:'#64748B', padding:20 }}>Unable to load model card.</div>
      ) : (
        <div style={{ display:'flex', gap:32 }}>
          {/* Left column */}
          <div style={{ flex:1, minWidth:0 }}>
            {/* Identity */}
            <div style={{ marginBottom:20, padding:'12px 16px', background:'#E4EAF4',
              borderRadius:8, borderLeft:'3px solid var(--or)' }}>
              <div style={{ fontSize:18, fontWeight:700, marginBottom:4 }}>
                {card.model?.display_name}
                {card.is_champion && (
                  <span className="badge b-or" data-tour="models-champion" style={{ marginLeft:10, fontSize:11 }}>★ Champion</span>
                )}
              </div>
              <div style={{ fontSize:12, color:'#64748B' }}>
                v{card.version_number}
                {card.version_label ? ` · ${card.version_label}` : ''}
                {' · '}{card.model?.family_code}
              </div>
              <div style={{ fontSize:11, color:'#64748B', marginTop:4 }}>
                {card.project} · {card.client}
              </div>
            </div>

            <Section title="Metrics">
              {Object.entries(card.metrics || {}).length === 0 ? (
                <div style={{ fontSize:12, color:'#94A3B8', padding:'4px 0' }}>
                  No metrics recorded for this run.
                </div>
              ) : Object.entries(card.metrics || {}).map(([k, v]) => {
                if (v == null) return null;
                const PCT_KEYS = new Set(['f1_score','accuracy','precision','recall']);
                const label = k.replace(/_/g,' ');
                const value = PCT_KEYS.has(k)
                  ? `${(v * 100).toFixed(2)}%`
                  : typeof v === 'number' && v > 1
                    ? v.toFixed(4)
                    : String(v);
                return <Row key={k} label={label} value={value} />;
              })}
            </Section>

            <Section title="Training">
              <Row label="Duration"     value={card.training?.duration} />
              <Row label="Started"      value={card.training?.started_at?.slice(0,19)?.replace('T',' ')} />
              <Row label="Completed"    value={card.training?.completed_at?.slice(0,19)?.replace('T',' ')} />
              <Row label="Retry count"  value={card.training?.retry_count > 0 ? card.training.retry_count : null} />
              {card.training?.error && (
                <div style={{ fontSize:11, color:'#dc2626', background:'rgba(220,38,38,.08)',
                  padding:'8px 10px', borderRadius:6, marginTop:6 }}>
                  {card.training.error}
                </div>
              )}
            </Section>
          </div>

          {/* Right column */}
          <div style={{ flex:1, minWidth:0 }}>
            <Section title="Model">
              <Row label="Family"       value={card.model?.family} />
              <Row label="Base model"   value={card.model?.base_model} />
              <Row label="Promoted"     value={card.promoted_at?.slice(0,10)} />
            </Section>

            <Section title="Params">
              {Object.keys(card.params || {}).length === 0 ? (
                <div style={{ color:'#64748B', fontSize:12 }}>Default params</div>
              ) : (
                Object.entries(card.params).map(([k, v]) => (
                  <Row key={k} label={k} value={v} mono
                    /* highlight overridden params */
                  />
                ))
              )}
            </Section>

            {card.artifact && (
              <Section title="Artifact location">
                <Row label="Server"  value={card.artifact.server} />
                <Row label="Path"    value={card.artifact.path} mono />
              </Section>
            )}

            {(card.fingerprints?.script_hash || card.fingerprints?.params_hash || card.fingerprints?.result_checksum) && (
              <Section title="Fingerprints">
                <Row label="Script hash"  value={card.fingerprints?.script_hash?.slice(0,16)}  mono />
                <Row label="Params hash"  value={card.fingerprints?.params_hash?.slice(0,16)}  mono />
                <Row label="Result cksum" value={card.fingerprints?.result_checksum?.slice(0,16)} mono />
              </Section>
            )}
          </div>
        </div>
      )}
      </ModalBody>
    </Modal>
  );
}

const CLASSICAL_CODES = new Set(['XGBOOST','RANDOM_FOREST','LIGHTGBM','CATBOOST','LOGISTIC_REG','SVM','GRADIENT_BOOSTING']);

export default function ModelsPage({ projectId, onNavigate }) {
  const { data: configs, loading, reload } = useApi(
    projectId ? `/projects/${projectId}/models` : null
  );
  const { data: families } = useApi('/meta/model-families');
  const { data: daptArtifacts } = useApi(
    projectId ? `/models/dapt-artifacts?project_id=${projectId}` : null
  );
  const [tab,             setTab]             = useState('models'); // 'models' | 'algos'
  const [showAdd,         setShowAdd]         = useState(false);
  const [expanded,        setExpanded]        = useState({});
  const [showParams,      setShowParams]      = useState(null);
  const [showCard,        setShowCard]        = useState(null);
  const [editCfg,         setEditCfg]         = useState(null);
  const [showVersionRuns, setShowVersionRuns] = useState(null);

  if (!projectId) return (
    <div className="page">
      <EmptyState icon="◉" title="Select a project" subtitle="Choose a project to manage its models"
        action={<button className="btn btn-primary btn-sm" onClick={() => onNavigate('projects')}>Go to Projects</button>} />
    </div>
  );

  const toggle = (id) => setExpanded(e => ({...e,[id]:!e[id]}));

  const modelConfigs = (configs||[]).filter(c => !CLASSICAL_CODES.has(c.family_code));
  const algoConfigs  = (configs||[]).filter(c =>  CLASSICAL_CODES.has(c.family_code));

  const modelFamilies = (families||[]).filter(f => !CLASSICAL_CODES.has(f.family_code));
  const algoFamilies  = (families||[]).filter(f =>  CLASSICAL_CODES.has(f.family_code));

  const activeConfigs  = tab === 'models' ? modelConfigs : algoConfigs;
  const activeFamilies = tab === 'models' ? modelFamilies : algoFamilies;

  const tabStyle = (t) => ({
    padding: '8px 20px', fontSize: 13, fontWeight: 600,
    border: 'none', cursor: 'pointer', borderRadius: 'var(--radius-sm)',
    background: tab === t ? 'var(--or)' : 'transparent',
    color:      tab === t ? '#fff' : 'var(--text-muted)',
    transition: 'all .15s',
  });

  return (
    <div className="page">
      <div className="page-hd">
        <div style={{ display:'flex', alignItems:'center', gap:4,
          background:'var(--surface-alt)', borderRadius:'var(--radius-md)',
          padding:3, border:'1px solid var(--border)',
        }}>
          <button style={tabStyle('models')} onClick={() => setTab('models')}>
            AI/ML Models
            {modelConfigs.length > 0 && (
              <span style={{ marginLeft:6, fontSize:10, background:'rgba(255,255,255,0.2)',
                padding:'1px 6px', borderRadius:9 }}>{modelConfigs.length}</span>
            )}
          </button>
          <button style={tabStyle('algos')} onClick={() => setTab('algos')}>
            Algorithms
            {algoConfigs.length > 0 && (
              <span style={{ marginLeft:6, fontSize:10, background:'rgba(255,255,255,0.2)',
                padding:'1px 6px', borderRadius:9 }}>{algoConfigs.length}</span>
            )}
          </button>
        </div>
        <button className="btn btn-primary btn-sm" onClick={() => setShowAdd(true)}>
          + Add {tab === 'models' ? 'Model' : 'Algorithm'}
        </button>
      </div>

      {/* Tab description */}
      <div style={{ fontSize:12, color:'var(--text-muted)', marginBottom:6, marginTop:-4 }}>
        {tab === 'models'
          ? 'Transformer and LLM families — for text classification, NER, domain pre-training, and LLM fine-tuning.'
          : 'Classical ML algorithms — for structured/tabular data. XGBoost, Random Forest, and similar.'}
      </div>
      <div style={{ fontSize:11.5, color:'var(--text-muted)', marginBottom:16, display:'flex', alignItems:'center' }}>
        Each card below is a <strong style={{ margin:'0 3px' }}>model config</strong> - a saved family + base model
        combination. Every time you train it, a new numbered <strong style={{ margin:'0 3px' }}>version</strong> is
        added underneath.
        <Hint text="Think of a config as the recipe (which family, which base model) and a version as one specific bake of it - each training run produces a new version with its own metrics, so you can compare v1 vs v2 vs v3 without losing earlier results.">
          <span />
        </Hint>
      </div>

      {loading ? (
        <div className="flex" style={{justifyContent:'center',padding:48}}><Spinner size={28}/></div>
      ) : activeConfigs.length === 0 ? (
        <EmptyState
          icon={tab === 'models' ? '◉' : '⚙'}
          title={tab === 'models' ? 'No models configured' : 'No algorithms configured'}
          subtitle={tab === 'models'
            ? 'Add a transformer or LLM family to start training on text data'
            : 'Add a classical ML algorithm for structured / tabular data'}
          action={<button className="btn btn-primary btn-sm" onClick={() => setShowAdd(true)}>
            + Add {tab === 'models' ? 'Model' : 'Algorithm'}
          </button>}
        />
      ) : (
        <div data-tour="models-list" style={{ display:'flex', flexDirection:'column', gap:10 }}>
          {activeConfigs.map(cfg => (
            <ModelConfigCard
              key={cfg.id}
              config={cfg}
              projectId={projectId}
              expanded={expanded[cfg.id]}
              onToggle={() => toggle(cfg.id)}
              onReload={reload}
              onShowParams={(fid,vid) => setShowParams({family_id:fid,version_id:vid})}
              onShowCard={(vid) => setShowCard(vid)}
              onEdit={(cfg) => setEditCfg(cfg)}
              onShowVersionRuns={(vid,vnum) => setShowVersionRuns({version_id:vid,version_number:vnum})}
            />
          ))}
        </div>
      )}

      {/* Domain-adapted models produced by DOMAIN_PRETRAIN runs. These are
          NOT model configs like the cards above - they're raw checkpoints,
          only usable as the starting point (base model) for a new config's
          Classification or NER run. The intro line below states that clearly
          up front instead of leaving it as a footnote after the list. */}
      {(daptArtifacts||[]).length > 0 && (
        <div style={{ marginTop:28, paddingTop:20, borderTop:'1px solid var(--border)' }}>
          <div style={{
            fontSize:11, fontWeight:700, textTransform:'uppercase',
            letterSpacing:'0.08em', color:'var(--success)', marginBottom:4,
            display:'flex', alignItems:'center', gap:8,
          }}>
            <span>🧠</span> Domain-adapted Checkpoints
            <span style={{
              fontSize:10, fontWeight:500, padding:'1px 6px', borderRadius:4,
              background:'rgba(22,163,74,0.08)', color:'var(--success)',
            }}>{(daptArtifacts||[]).length}</span>
            <Hint text="Produced by a Domain Pre-training run. Not a model config on its own - select one as the Base Model when creating a new Classification or NER config to start from weights already adapted to your data.">
              <span />
            </Hint>
          </div>
          <div style={{ fontSize:11.5, color:'var(--text-muted)', marginBottom:12 }}>
            Raw checkpoints, not model configs. Use one as the base model when adding a new Classification or NER model above.
          </div>
          <div style={{ display:'flex', flexDirection:'column', gap:8 }}>
            {(daptArtifacts||[]).map(a => (
              <div key={a.registry_id} style={{
                padding:'12px 16px', borderRadius:'var(--radius-md)',
                border:'1px solid rgba(22,163,74,0.2)',
                background:'rgba(22,163,74,0.03)',
              }}>
                <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start' }}>
                  <div>
                    <div style={{ fontSize:13, fontWeight:600, color:'var(--text-primary)', marginBottom:3 }}>
                      {a.dapt_run_label || `DAPT Run #${a.dapt_run_number}`}
                      <span style={{
                        marginLeft:8, fontSize:10, padding:'1px 6px', borderRadius:4,
                        background:'rgba(22,163,74,0.1)', color:'var(--success)',
                      }}>{a.family_code}</span>
                    </div>
                    <div style={{ fontSize:11, color:'var(--text-muted)' }}>
                      {a.artifact_server_name ? `Server: ${a.artifact_server_name} · ` : ''}
                      {a.produced_at ? `Produced: ${new Date(a.produced_at).toLocaleDateString()}` : ''}
                    </div>
                    <div style={{ fontSize:10, color:'var(--text-muted)', marginTop:4, fontFamily:'var(--font-mono)', wordBreak:'break-all' }}>
                      {a.artifact_path}
                    </div>
                  </div>
                  <div style={{ display:'flex', gap:16, textAlign:'right', flexShrink:0, marginLeft:16 }}>
                    {a.mlm_loss != null && (
                      <div>
                        <div style={{ fontSize:13, fontWeight:700 }}>{a.mlm_loss.toFixed(4)}</div>
                        <div style={{ fontSize:10, color:'var(--text-muted)' }}>MLM Loss</div>
                      </div>
                    )}
                    {a.perplexity != null && (
                      <div>
                        <div style={{ fontSize:13, fontWeight:700 }}>{a.perplexity.toFixed(2)}</div>
                        <div style={{ fontSize:10, color:'var(--text-muted)' }}>Perplexity</div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {editCfg && (
        <EditModelModal
          projectId={projectId}
          config={editCfg}
          onClose={() => setEditCfg(null)}
          onDone={() => { reload(); setEditCfg(null); }}
        />
      )}

      {showAdd && (
        <AddModelModal
          projectId={projectId}
          families={activeFamilies}
          mode={tab}
          existing={(configs||[]).map(c=>c.family_code)}
          onClose={() => setShowAdd(false)}
          onDone={() => { reload(); setShowAdd(false); }}
        />
      )}

      {showVersionRuns && (
        <VersionRunsModal
          versionId={showVersionRuns.version_id}
          versionNumber={showVersionRuns.version_number}
          onClose={() => setShowVersionRuns(null)}
        />
      )}
      {showParams && (
        <ParamsModal
          familyId={showParams.family_id}
          versionId={showParams.version_id}
          onClose={() => setShowParams(null)}
          onSaved={() => { setShowParams(null); reload(); }}
        />
      )}
      {showCard && (
        <ModelCardModal versionId={showCard} onClose={() => setShowCard(null)} />
      )}
    </div>
  );
}

/* ── Model Config Card ─────────────────────────────────────────────────────── */
function ModelConfigCard({ config, projectId, expanded, onToggle, onReload, onShowParams, onShowVersionRuns, onShowCard, onEdit }) {
  const { data: versions, loading: vLoad, reload: vReload } = useApi(
    expanded ? `/projects/${projectId}/models/${config.id}/versions` : null
  );
  const [creating, setCreating] = useState(false);

  const createVersion = async () => {
    setCreating(true);
    try {
      await apiFetch(`/projects/${projectId}/models/${config.id}/versions`, { method:'POST', body:JSON.stringify({}) });
      vReload();
    } catch(e) { alert(e.message); }
    finally { setCreating(false); }
  };

  const promote = async (vid) => {
    try {
      await apiFetch(`/versions/${vid}/promote`, { method:'POST' });
      vReload(); onReload();
    } catch(e) { alert(e.message); }
  };

  return (
    <div className="card">
      {/* Header */}
      <div style={{ padding:'14px 18px', display:'flex', alignItems:'center', gap:12, cursor:'pointer' }}
        onClick={onToggle}>
        <div style={{ width:10, height:10, borderRadius:3, background:'var(--or)', flexShrink:0 }} />
        <div style={{ flex:1 }}>
          <div className="flex gap8">
            <span className="bold" style={{ fontSize:14 }}>{config.display_name}</span>
            <span className="badge b-gray">{config.family_code}</span>
            {config.best_f1 && (
              <Hint text="The highest F1 score among all versions of this config.">
                <span className="badge b-green">Best F1: {(config.best_f1*100).toFixed(1)}%</span>
              </Hint>
            )}
          </div>
          <div className="xs c-3 mt2">{config.base_model_name}</div>
        </div>
        <div className="flex gap10">
          <span className="xs c-3">{config.total_versions || 0} versions</span>
          <button className="btn btn-ghost btn-xs" title="Edit name or base model"
            style={{ padding:'2px 6px' }}
            onClick={e => { e.stopPropagation(); onEdit(config); }}>
            ✎
          </button>
          <button className="btn btn-ghost btn-xs" title="Delete model"
            style={{ color:'var(--red)', opacity:.7, padding:'2px 6px' }}
            onClick={async e => {
              e.stopPropagation();
              if (!window.confirm(`Delete model "${config.display_name}" and all its versions? This cannot be undone.`)) return;
              try {
                await apiFetch(`/projects/${config.project_id}/models/${config.id}`, { method:'DELETE' });
                onReload();
              } catch(err) { alert(err.message); }
            }}>✕</button>
          <span style={{ color:'#64748B', fontSize:12 }}>{expanded ? '▲' : '▼'}</span>
        </div>
      </div>

      {/* Versions list */}
      {expanded && (
        <div style={{ borderTop:'1px solid var(--bd)' }}>
          <div style={{ padding:'10px 18px', display:'flex', alignItems:'center', justifyContent:'space-between' }}>
            <span className="xs c-3" style={{ fontWeight:600, textTransform:'uppercase', letterSpacing:'.07em' }}>Versions</span>
            <button className="btn btn-ghost btn-xs" onClick={createVersion} disabled={creating}>
              {creating ? <Spinner size={10}/> : '+ New Version'}
            </button>
          </div>

          {vLoad ? (
            <div style={{ padding:'16px 18px' }}><Spinner size={18}/></div>
          ) : (versions||[]).length === 0 ? (
            <div style={{ padding:'16px 18px', textAlign:'center' }}>
              <div className="xs c-3">No versions yet.</div>
              <button className="btn btn-primary btn-xs mt8" onClick={createVersion} disabled={creating}>
                Create First Version
              </button>
            </div>
          ) : (
            <div className="tbl-wrap">
              <table>
                <thead>
                  <tr>
                    <th>Version</th>
                    <th>Status</th>
                    <th>F1</th>
                    <th>Accuracy</th>
                    <th>
                      <Hint text="The Champion version is the one currently marked as this config's best/production version - promote a version to make it the champion.">
                        <span>Champion</span>
                      </Hint>
                    </th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {(versions||[]).map(v => (
                    <tr key={v.id}>
                      <td>
                        <span className="bold">v{v.version_number}</span>
                        {v.version_label && <span className="xs c-3 ml8">{v.version_label}</span>}
                        {v.parent_version_id && (
                          <span className="badge b-slate" style={{marginLeft:6,fontSize:9}}>branched</span>
                        )}
                      </td>
                      <td><StatusBadge code={v.status_code} /></td>
                      <td className="td-mono">
                        {v.f1_score != null ? (
                          <span style={{ color:'var(--green)', fontWeight:600 }}>
                            {(v.f1_score*100).toFixed(2)}%
                          </span>
                        ) : '-'}
                      </td>
                      <td className="td-mono">
                        {v.accuracy != null ? `${(v.accuracy*100).toFixed(1)}%` : '-'}
                      </td>
                      <td>
                        {v.is_champion && <span className="badge b-or">★ Champion</span>}
                      </td>
                      <td>
                        <div className="flex gap6">
                          <button className="btn btn-ghost btn-xs"
                            onClick={() => onShowParams(config.family_id, v.id)}>
                            Params
                          </button>
                          {v.status_code === 'COMPLETED' && (
                            <button className="btn btn-ghost btn-xs"
                              onClick={() => onShowCard && onShowCard(v.id)} data-tour="models-card">
                              Card
                            </button>
                          )}
                          <button className="btn btn-ghost btn-xs"
                            onClick={() => onShowVersionRuns && onShowVersionRuns(v.id, v.version_number)}>
                            Runs
                          </button>
                          {v.status_code === 'COMPLETED' && !v.is_champion && (
                            <button className="btn btn-success btn-xs" onClick={() => promote(v.id)}>
                              ★ Promote
                            </button>
                          )}
                          {!v.is_champion && (
                            <button className="btn btn-ghost btn-xs"
                              style={{ color:'var(--red)', opacity:.7 }}
                              title="Delete this version"
                              onClick={async e => {
                                e.stopPropagation();
                                if (!window.confirm(`Delete v${v.version_number}? This cannot be undone.`)) return;
                                try {
                                  await apiFetch(`/versions/${v.id}`, { method:'DELETE' });
                                  vReload(); onReload();
                                } catch(err) { alert(err.message); }
                              }}>✕</button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

/* ── Add Model Modal ───────────────────────────────────────────────────────── */
// Known local model paths per family code (load from server cache, not HuggingFace)
const CLASSICAL_FAMILIES = new Set(['XGBOOST','RANDOM_FOREST','LIGHTGBM','CATBOOST','LOGISTIC_REG','SVM','GRADIENT_BOOSTING']);
const LLM_FAMILIES        = new Set(['MISTRAL_7B','LLAMA3_8B','PHI3_MINI','QWEN25','GEMMA2','DEEPSEEK_R1_7B','DEEPSEEK_R1_8B','QWEN25_CODER','DEEPSEEK_CODER','KIMI_K2']);
const LLM_MODEL_PATHS = {
  MISTRAL_7B:       '/var/aifab/models/mistral-7b-instruct',
  LLAMA3_8B:        '/var/aifab/models/llama-3-8b-instruct',
  PHI3_MINI:        '/var/aifab/models/phi-3-mini-3.8b',
  QWEN25:           '/var/aifab/models/qwen2.5-7b-instruct',
  GEMMA2:           '/var/aifab/models/gemma-2-9b-instruct',
  DEEPSEEK_R1_7B:   '/var/aifab/models/deepseek-r1-7b',
  DEEPSEEK_R1_8B:   '/var/aifab/models/deepseek-r1-8b',
  QWEN25_CODER:     '/var/aifab/models/qwen2.5-coder-7b',
  DEEPSEEK_CODER:   '/var/aifab/models/deepseek-coder-7b',
};

function EditModelModal({ projectId, config, onClose, onDone }) {
  const [displayName, setDisplayName] = useState(config.display_name || '');
  const [baseModel,   setBaseModel]   = useState(config.base_model_name || '');
  const [catalog,     setCatalog]     = useState([]);
  const [saving,      setSaving]      = useState(false);
  const [err,         setErr]         = useState(null);
  const trained = (config.total_versions || 0) > 0 && config.best_f1 != null;

  useEffect(() => {
    if (!config.family_id) return;
    apiFetch(`/models/families/${config.family_id}/catalog`)
      .then(r => setCatalog(r.models || []))
      .catch(() => setCatalog([]));
  }, [config.family_id]);

  const save = async () => {
    setSaving(true); setErr(null);
    try {
      await apiFetch(`/projects/${projectId}/models/${config.id}`, {
        method: 'PATCH',
        body: JSON.stringify({ display_name: displayName, base_model_name: baseModel }),
      });
      onDone();
    } catch (e) { setErr(e.message); }
    finally { setSaving(false); }
  };

  return (
    <Modal title="Edit Model" subtitle="Fix the name or a wrongly selected base model" onClose={onClose}>
      <ModalBody>
        <Err msg={err} />
        <div className="form-grp">
          <label className="form-lbl">Display Name</label>
          <input className="form-inp" value={displayName} onChange={e => setDisplayName(e.target.value)} />
        </div>
        <div className="form-grp">
          <label className="form-lbl">Base Model</label>
          {catalog.length > 0 ? (
            <select className="form-inp" value={baseModel} onChange={e => setBaseModel(e.target.value)}>
              {catalog.map(m => (
                <option key={m.model_name} value={m.model_name}>
                  {m.model_name}{m.source_type === 'DAPT_ARTIFACT' ? ' (domain-adapted)' : ''}
                </option>
              ))}
            </select>
          ) : (
            <input className="form-inp" value={baseModel} onChange={e => setBaseModel(e.target.value)} />
          )}
          <div className="form-hint">
            {trained
              ? 'This model has trained versions - the base model can no longer be changed, only the name.'
              : 'Changing the base is allowed until a version completes training.'}
          </div>
        </div>
      </ModalBody>
      <ModalFooter>
        <button className="btn btn-ghost btn-sm" onClick={onClose}>Cancel</button>
        <button className="btn btn-primary btn-sm" onClick={save} disabled={saving}>
          {saving ? <Spinner size={12}/> : 'Save Changes'}
        </button>
      </ModalFooter>
    </Modal>
  );
}

function AddModelModal({ projectId, families, mode, existing, onClose, onDone }) {
  const [familyId,    setFamilyId]   = useState('');
  const [displayName, setDisplayName] = useState('');
  const [baseModel,   setBaseModel]  = useState('');
  const [saving,      setSaving]     = useState(false);
  const [err,         setErr]        = useState(null);
  const [catalog,     setCatalog]    = useState([]);   // registered base models for the family

  useEffect(() => {
    if (!familyId) { setCatalog([]); return; }
    apiFetch(`/models/families/${familyId}/catalog`)
      .then(r => setCatalog(r.models || []))
      .catch(() => setCatalog([]));
  }, [familyId]);

  const available = families;

  const selectedFamily = families.find(f => f.id === +familyId);
  const isClassical    = selectedFamily && CLASSICAL_FAMILIES.has(selectedFamily.family_code);
  const isLLM          = selectedFamily && LLM_FAMILIES.has(selectedFamily.family_code);

  const onFamilyChange = (id) => {
    setFamilyId(id);
    const f = families.find(x => x.id === +id);
    if (!f) return;
    setDisplayName(f.family_name);
    // Auto-set base model so user never needs to think about it for non-transformer families
    if (CLASSICAL_FAMILIES.has(f.family_code)) {
      setBaseModel(f.family_code); // classical uses family_code as the algo selector
    } else if (LLM_FAMILIES.has(f.family_code)) {
      setBaseModel(LLM_MODEL_PATHS[f.family_code] || '');
    } else {
      setBaseModel(''); // transformer — user must enter HuggingFace ID
    }
  };

  const save = async () => {
    // Classical: base_model is auto-set; Transformer: user must fill it
    if (!familyId || !displayName) { setErr('All fields required'); return; }
    if (!isClassical && !baseModel) { setErr('Base model name is required'); return; }
    setSaving(true); setErr(null);
    try {
      await apiFetch(`/projects/${projectId}/models`, {
        method:'POST',
        body:JSON.stringify({ family_id:+familyId, display_name:displayName, base_model_name:baseModel }),
      });
      onDone();
    } catch(e) { setErr(e.message); }
    finally { setSaving(false); }
  };

  const isAlgoMode = mode === 'algos';
  return (
    <Modal title={isAlgoMode ? "Add Algorithm" : "Add Model"} subtitle={isAlgoMode ? "Choose a classical ML algorithm for structured data" : "Choose a model family and configure it for this project"} onClose={onClose}>
      <ModalBody>
        <Err msg={err} />

        {available.length === 0 ? (
          <Alert type="info">All available model families are already configured for this project.</Alert>
        ) : (
          <>
            <div className="form-grp mb16">
              <label className="form-lbl">Model Family *</label>
              <select className="form-inp" value={familyId} onChange={e => onFamilyChange(e.target.value)}>
                <option value="">- Select Family -</option>
                {available.map(f => (
                  <option key={f.id} value={f.id}>
                    {f.family_name} ({f.family_code})
                    {f.cpu_feasible ? ' · CPU' : ' · GPU required'}
                  </option>
                ))}
              </select>
            </div>

            {selectedFamily && (
              <div style={{ background:'#E4EAF4', borderRadius:'8px', padding:14, marginBottom:16 }}>
                <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr 1fr', gap:10 }}>
                  {[
                    ['Min RAM', `${selectedFamily.min_ram_gb} GB`],
                    ['Rec RAM', `${selectedFamily.rec_ram_gb} GB`],
                    ['CPU', selectedFamily.cpu_feasible ? '✓ feasible' : '✗ needs GPU'],
                  ].map(([l,v]) => (
                    <div key={l}>
                      <div className="xs c-3" style={{ fontWeight:600, marginBottom:2 }}>{l}</div>
                      <div className="sm bold">{v}</div>
                    </div>
                  ))}
                </div>
                {selectedFamily.use_case && (
                  <div className="xs c-3 mt10" style={{ lineHeight:1.5 }}>{selectedFamily.use_case}</div>
                )}
                {selectedFamily.supported_purposes && selectedFamily.supported_purposes.length > 0 && (
                  <div style={{ marginTop:10 }}>
                    <div className="xs c-3" style={{ marginBottom:5, fontWeight:600, textTransform:'uppercase', letterSpacing:'0.06em' }}>Supported run purposes</div>
                    <div style={{ display:'flex', flexWrap:'wrap', gap:5 }}>
                      {selectedFamily.supported_purposes.map(p => (
                        <span key={p} style={{
                          fontSize:10, fontWeight:600, padding:'2px 8px', borderRadius:3,
                          background: {
                            CLASSIFICATION:  'rgba(232,74,0,0.1)',
                            DOMAIN_PRETRAIN: 'rgba(34,197,94,0.1)',
                            REGRESSION:      'rgba(59,130,246,0.1)',
                            NER:             'rgba(139,92,246,0.1)',
                            CUSTOM:          'rgba(255,255,255,0.06)',
                          }[p] || 'rgba(255,255,255,0.06)',
                          color: {
                            CLASSIFICATION:  'var(--or)',
                            DOMAIN_PRETRAIN: '#4ade80',
                            REGRESSION:      '#60a5fa',
                            NER:             '#a78bfa',
                            CUSTOM:          '#334155',
                          }[p] || '#334155',
                        }}>
                          {{
                            CLASSIFICATION:  'Classification',
                            DOMAIN_PRETRAIN: 'Domain Pre-training',
                            REGRESSION:      'Regression',
                            NER:             'NER',
                            CUSTOM:          'Custom',
                          }[p] || p}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}

            <div className="form-row form-row-2">
              <div className="form-grp">
                <label className="form-lbl">Display Name *</label>
                <input className="form-inp" value={displayName} onChange={e => setDisplayName(e.target.value)}
                  placeholder={isClassical ? 'e.g. XGBoost Ticket Classifier' : 'e.g. DistilBERT Classifier'} />
              </div>

              {/* Base model field — behaviour depends on family group */}
              {isClassical ? (
                <div className="form-grp">
                  <label className="form-lbl">Algorithm</label>
                  <div className="form-inp" style={{ background:'#F7F9FC', color:'#64748B', cursor:'not-allowed', display:'flex', alignItems:'center' }}>
                    {selectedFamily?.family_name}
                  </div>
                  <div className="form-hint">
                    Classical ML algorithms train entirely from your data — no pre-trained weights are loaded.
                  </div>
                </div>
              ) : isLLM ? (
                <div className="form-grp">
                  <label className="form-lbl">Model Path (on your server)</label>
                  <input className="form-inp" value={baseModel} onChange={e => setBaseModel(e.target.value)}
                    placeholder="/var/aifab/models/..." />
                  <div className="form-hint">
                    Path to the model weights on your execution server. Models must be downloaded to your server before training.
                  </div>
                </div>
              ) : (
                <div className="form-grp">
                  <label className="form-lbl">Base Model *</label>
                  <select className="form-inp" value={baseModel} onChange={e => setBaseModel(e.target.value)}>
                    <option value="">- Select base model -</option>
                    {catalog.map(m => (
                      <option key={m.model_name} value={m.model_name}>
                        {m.model_name}{m.source_type === 'DAPT_ARTIFACT' ? ' (domain-adapted)' : ''}
                      </option>
                    ))}
                  </select>
                  <div className="form-hint">
                    {catalog.length === 0
                      ? 'No models registered for this family yet - add one in the Model Catalog.'
                      : 'Selected from the Model Catalog - typos are no longer possible here.'}
                  </div>
                </div>
              )}
            </div>
          </>
        )}
      </ModalBody>
      <ModalFooter>
        <button className="btn btn-ghost btn-sm" onClick={onClose}>Cancel</button>
        {available.length > 0 && (
          <button className="btn btn-primary btn-sm" onClick={save} disabled={saving || !familyId}>
            {saving ? <Spinner size={12}/> : 'Add Model'}
          </button>
        )}
      </ModalFooter>
    </Modal>
  );
}

/* ── Params Modal ──────────────────────────────────────────────────────────── */
function ParamsModal({ familyId, versionId, onClose, onSaved }) {
  const { data: categories, loading } = useApi(`/models/families/${familyId}/params`);
  const { data: savedValues, loading: _savedLoading } = useApi(`/versions/${versionId}/params`);
  const [values,       setValues]      = useState({});
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [saving,       setSaving]      = useState(false);
  const [err,          setErr]         = useState(null);

  // Initialise form values from saved DB values once loaded
  useEffect(() => {
    if (savedValues && Object.keys(savedValues).length > 0) {
      setValues(savedValues);
    }
  }, [savedValues]);

  const set = (key, val) => setValues(v => ({...v,[key]:String(val)}));

  const save = async () => {
    setSaving(true); setErr(null);
    try {
      await apiFetch(`/versions/${versionId}/params`, {
        method:'PATCH', body:JSON.stringify({ updates: values }),
      });
      onSaved();
    } catch(e) { setErr(e.message); }
    finally { setSaving(false); }
  };

  return (
    <Modal title="Hyperparameters" onClose={onClose} wide>
      <ModalBody>
        {loading ? (
          <div className="flex" style={{justifyContent:'center',padding:32}}><Spinner/></div>
        ) : (
          <>
            <Err msg={err}/>
            <div className="flex-btw mb16">
              <span className="xs c-3">Defaults shown - change what you need</span>
              <label className="flex gap8 sm" style={{ cursor:'pointer' }}>
                <input type="checkbox" checked={showAdvanced} onChange={e => setShowAdvanced(e.target.checked)}/>
                Show advanced
              </label>
            </div>

            {(categories||[]).map(cat => {
              const visible = cat.params.filter(p => showAdvanced || !p.is_advanced);
              if (!visible.length) return null;
              return (
                <div key={cat.category_code} className="mb20">
                  <div style={{ fontSize:11, fontWeight:700, letterSpacing:'.08em', textTransform:'uppercase', color:'#334155', marginBottom:10, paddingBottom:6, borderBottom:'1px solid var(--bd)' }}>
                    {cat.category_name}
                  </div>
                  {visible.map(p => {
                    const val = values[p.param_key] ?? p.default_value;
                    return (
                      <div key={p.param_key} className="param-row">
                        <div>
                          <div className="param-name">{p.param_name}</div>
                          <div className="xs c-3">{p.description}</div>
                          {p.impact === 'HIGH' && <div className="xs c-am" style={{marginTop:2}}>⚠ High impact</div>}
                        </div>
                        <div className="param-ctrl">
                          {p.ui_component === 'TOGGLE' ? (
                            <label className="flex gap8 sm" style={{ cursor:'pointer' }}>
                              <input type="checkbox" checked={val === 'true'}
                                onChange={e => set(p.param_key, e.target.checked)} />
                              {val === 'true' ? 'Enabled' : 'Disabled'}
                            </label>
                          ) : p.ui_component === 'DROPDOWN' ? (
                            <select className="form-inp" value={val} onChange={e => set(p.param_key, e.target.value)}>
                              {(p.allowed_values||[p.default_value]).map(v => (
                                <option key={v} value={v}>{v}</option>
                              ))}
                            </select>
                          ) : p.ui_component === 'SLIDER' && p.min_value != null ? (
                            <input type="range" value={val}
                              min={p.min_value} max={p.max_value} step={p.step_value||1}
                              onChange={e => set(p.param_key, e.target.value)} />
                          ) : (
                            <input className="form-inp" value={val}
                              onChange={e => set(p.param_key, e.target.value)} />
                          )}
                        </div>
                        <div className="param-val">
                          {val}{p.unit ? ` ${p.unit}` : ''}
                        </div>
                      </div>
                    );
                  })}
                </div>
              );
            })}
          </>
        )}
      </ModalBody>
      <ModalFooter>
        <button className="btn btn-ghost btn-sm" onClick={onClose}>Cancel</button>
        <button className="btn btn-primary btn-sm" onClick={save} disabled={saving||loading}>
          {saving ? <Spinner size={12}/> : 'Save Parameters'}
        </button>
      </ModalFooter>
    </Modal>
  );
}


/* ── Version Runs Modal ─────────────────────────────────────────────────────── */
function VersionRunsModal({ versionId, versionNumber, onClose }) {
  const { data: runs, loading } = useApi(`/versions/${versionId}/runs`);
  return (
    <Modal title={`Runs using Model Version v${versionNumber}`} onClose={onClose}>
      {loading ? (
        <div className="flex" style={{justifyContent:'center',padding:32}}><Spinner size={24}/></div>
      ) : (!runs || runs.length === 0) ? (
        <div className="c-sl sm" style={{padding:16}}>No runs have used this version yet.</div>
      ) : (
        <table style={{width:'100%',borderCollapse:'collapse'}}>
          <thead>
            <tr style={{borderBottom:'1px solid var(--bdr)'}}>
              {['Run','Label','Status','Dataset','Dataset v','Feature Config','Target','F1'].map(h => (
                <th key={h} className="xs c-sl" style={{textAlign:'left',padding:'6px 10px',fontWeight:600}}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {runs.map(r => (
              <tr key={r.run_id} style={{borderBottom:'1px solid var(--bdr)'}}>
                <td style={{padding:'7px 10px'}}><span className="c-or mono">#{r.run_number}</span></td>
                <td style={{padding:'7px 10px'}} className="sm">{r.run_label || '-'}</td>
                <td style={{padding:'7px 10px'}}>
                  <span className={`badge b-${r.status==='COMPLETED'?'green':r.status==='FAILED'?'red':'slate'}`}>
                    {r.status}
                  </span>
                </td>
                <td style={{padding:'7px 10px'}} className="sm">{r.datasource || '-'}</td>
                <td style={{padding:'7px 10px'}} className="sm">
                  {r.datasource_version != null
                    ? <span className="badge b-or" style={{fontSize:9}}>v{r.datasource_version}</span>
                    : '-'}
                </td>
                <td style={{padding:'7px 10px'}} className="sm">{r.feature_config || '-'}</td>
                <td style={{padding:'7px 10px'}} className="mono sm c-or">{r.target_column || '-'}</td>
                <td style={{padding:'7px 10px'}} className="sm">
                  {r.f1_score ? <span style={{color:'var(--green)',fontWeight:600}}>{(r.f1_score*100).toFixed(1)}%</span> : '-'}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </Modal>
  );
}
